import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { StatusBar, Style } from "@capacitor/status-bar";

import { useAuth } from "../providers/AuthProvider";
import { createLogger } from "../utils/logger";

const logger = createLogger("ThemeProvider");

export type ThemeMode = "dark" | "light";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const THEME_STORAGE_KEY = "lotlocator:ui-theme";
const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

type InitialThemeState = {
  theme: ThemeMode;
  followSystem: boolean;
};

export function detectSystemTheme(): ThemeMode {
  if (typeof window === "undefined") {
    return "light";
  }
  const mediaQuery = window.matchMedia?.(DARK_MEDIA_QUERY);
  return mediaQuery?.matches ? "dark" : "light";
}

export function getInitialThemeState(): InitialThemeState {
  if (typeof window === "undefined") {
    return { theme: "light", followSystem: true };
  }

  const stored = window.sessionStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") {
    return { theme: stored, followSystem: false };
  }

  return { theme: detectSystemTheme(), followSystem: true };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, accessToken, updateProfile } = useAuth();
  const initialThemeState = useMemo<InitialThemeState>(() => getInitialThemeState(), []);
  const [theme, setThemeState] = useState<ThemeMode>(initialThemeState.theme);
  const [followSystem, setFollowSystem] = useState<boolean>(initialThemeState.followSystem);

  useEffect(() => {
    const preferred = user?.preferences?.appearance?.theme;
    if (!preferred) {
      return;
    }
    const canonical = preferred === "light" ? "light" : "dark";
    setThemeState((current) => (current === canonical ? current : canonical));
    setFollowSystem(false);
  }, [user?.preferences?.appearance?.theme]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      root.dataset.theme = theme;
      root.style.setProperty("color-scheme", theme);
      if (theme === "dark") {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
      const meta = document.getElementById("pm-theme-color") as HTMLMetaElement | null;
      if (meta) {
        meta.content = theme === "dark" ? "#0f172a" : "#f8fafc";
      }

      // Sync native status bar (no-op on web, active in Capacitor)
      StatusBar.setStyle({
        style: theme === "dark" ? Style.Dark : Style.Light,
      }).catch(() => {
        // Ignore errors when running in standard browser
      });
    }
  }, [theme]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (followSystem) {
        window.sessionStorage.removeItem(THEME_STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(THEME_STORAGE_KEY, theme);
      }
    }
  }, [followSystem, theme]);

  useEffect(() => {
    if (!followSystem || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setThemeState(event.matches ? "dark" : "light");
    };

    // Ensure we reflect the current system preference immediately.
    setThemeState(mediaQuery.matches ? "dark" : "light");

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    // Fallback for older browsers/Safari
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [followSystem]);

  const setTheme = useCallback(
    (mode: ThemeMode) => {
      const canonical = mode === "light" ? "light" : "dark";
      setThemeState(canonical);
       setFollowSystem(false);
      if (user && accessToken) {
        void updateProfile({
          preferences: {
            appearance: {
              theme: canonical,
            },
          },
        }).catch((error) => {
          logger.warn("Failed to persist theme preference", error);
        });
      }
    },
    [accessToken, updateProfile, user],
  );

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
}
